import React, {
  useEffect,
  useCallback,
  useRef,
  useState,
  useMemo,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "../context/SidebarContext";
import { UserGroupIcon } from "@heroicons/react/24/outline";
import { Contact } from "lucide-react";
import { useLogoSrc } from "../hooks/useLogoSrc";
import { MessageSquare } from "lucide-react";
// import { Building2 } from "lucide-react";
import {
  ChevronDownIcon,
  GridIcon,
  TableIcon,
  QuotationIcon,
  DollarLineIcon,
} from "../icons";
import { ProductIcon, ReportIcon } from "../components/svg/Icons";
import {
  asideStyles,
  logoContainerStyles,
  navStyles,
  menuListStyles,
  menuItemStyles,
  menuItemIconStyles,
  menuItemTextStyles,
  chevronStyles,
  submenuContainerStyles,
  submenuListStyles,
  submenuItemStyles,
} from "../styles/layout/AppSidebarStyles";
import { useSettings } from "../context/SettingsContext";

type NavItem = {
  name: string;
  icon: React.ReactNode;
  path?: string;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

const AppSidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const { settings, fetchSettings } = useSettings();
  const location = useLocation();
  const [isDarkMode, setIsDarkMode] = useState(false);
  const token = sessionStorage.getItem("access_token");
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const { logoSrc, displayName } = useLogoSrc();

  // Fetch user roles from sessionStorage on component mount
  useEffect(() => {
    const roles = sessionStorage.getItem("user_roles");
    if (roles) {
      try {
        const parsedRoles = JSON.parse(roles);
        setUserRoles(parsedRoles);
      } catch (error) {
        console.error("Error parsing user_roles from sessionStorage:", error);
        setUserRoles([]);
      }
    }
  }, []);

  // Fetch settings on mount to ensure latest colors are loaded
  useEffect(() => {
    if (token) {
      fetchSettings();
    }
  }, [fetchSettings, token]);

  // Check for dark mode
  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    };

    checkDarkMode();

    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });

    return () => observer.disconnect();
  }, []);

  const [openSubmenu, setOpenSubmenu] = useState<{
    type: "main" | "others";
    index: number;
  } | null>(null);
  const [, setSubMenuHeight] = useState<Record<string, number>>({});
  const subMenuRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const isActive = useCallback(
    (path: string) => {
      const basePath = path
        .split("/")[1]
        .replace("-list", "")
        .replace("-summary", "");

      if (path === "/lead-summary") {
        return (
          location.pathname === "/lead-summary" ||
          location.pathname === "/lead-summary-details"
        );
      }

      if (path === "/lead-list") {
        const isLeadRelated =
          location.pathname === path ||
          location.pathname === "/call-list" ||
          location.pathname.startsWith("/call-list/") ||
          location.pathname === "/lead-meeting" ||
          location.pathname.startsWith("/lead-meeting/") ||
          location.pathname === "/lead-task" ||
          location.pathname.startsWith("/lead-task/") ||
          location.pathname === `/${basePath}` ||
          location.pathname === `/${basePath}-add` ||
          location.pathname === `/${basePath}-edit` ||
          location.pathname === `/${basePath}-view` ||
          location.pathname === `/${basePath}-details` ||
          location.pathname === `/${basePath}-quotation-view` ||
          location.pathname.startsWith(`/${basePath}-edit/`) ||
          location.pathname.startsWith(`/${basePath}/`);
        return isLeadRelated;
      }

      return (
        location.pathname === path ||
        location.pathname === `/${basePath}` ||
        location.pathname === `/${basePath}-add` ||
        location.pathname === `/${basePath}-edit` ||
        location.pathname === `/${basePath}-view` ||
        location.pathname === `/${basePath}-details` ||
        location.pathname === `/${basePath}-quotation-view` ||
        location.pathname.startsWith(`/${basePath}-edit/`) ||
        location.pathname.startsWith(`/${basePath}/`)
      );
    },
    [location.pathname]
  );

  useEffect(() => {
    const heights: Record<string, number> = {};
    Object.entries(subMenuRefs.current).forEach(([key, el]) => {
      if (el) {
        heights[key] = el.scrollHeight;
      }
    });
    setSubMenuHeight(heights);
  }, []);

  // Memoize navItems to ensure stability and fix ESLint warning
  const navItems: NavItem[] = useMemo(
    () => {
      const items = [
        { icon: <GridIcon />, name: "Dashboard", path: "/" },
        // {
        //   icon: <Building2 className="h-5 w-5" />,
        //   name: "Company",
        //   path: "/company-details",
        // },
        {
          icon: <Contact className="h-6 w-6" />,
          name: "Contact List",
          path: "/contact-list",
        },
        {
          icon: <UserGroupIcon className="h-6 w-6" />,
          name: "Lead",
          path: "/lead-list",
        },
        {
          icon: <DollarLineIcon />,
          name: "Opportunity",
          path: "/opportunity-list",
        },
        { icon: <QuotationIcon />, name: "Quotation", path: "/quotation-list" },
        {
          name: "Reports",
          icon: <ReportIcon />,
          subItems: [{ name: "Lead Summary", path: "/lead-summary", pro: false }],
        },
      ];

      // Only include Master and Product modules for Admin users
      if (userRoles.includes("Admin")) {
        items.splice(6, 0,
          {
            name: "Master",
            icon: <TableIcon />,
            subItems: [
              { name: "User", path: "/user-list", pro: false },
              { name: "Job Title", path: "/job-title-list", pro: false },
              { name: "Source", path: "/source-list", pro: false },
              { name: "Stage", path: "/stage-list", pro: false },
              { name: "Status", path: "/status-list", pro: false },
            ],
          },
          {
            name: "Product",
            icon: <ProductIcon />,
            subItems: [
              { name: "Category", path: "/category-list", pro: false },
              { name: "Sub Category", path: "/subcategory-list", pro: false },
              { name: "Product", path: "/product-list", pro: false },
            ],
          },
          {
            icon: <MessageSquare className="h-6 w-6" />, // Updated icon
            name: "Chat",
            path: "/chat",
          },
        );
      }

      return items;
    },
    [userRoles]
  );

  // Update useEffect with navItems in dependency array
  useEffect(() => {
    const masterIndex = navItems.findIndex((item) => item.name === "Master");
    const productIndex = navItems.findIndex((item) => item.name === "Product");
    const reportsIndex = navItems.findIndex((item) => item.name === "Reports");

    const masterSubItems = navItems[masterIndex]?.subItems || [];
    const productSubItems = navItems[productIndex]?.subItems || [];
    const reportsSubItems = navItems[reportsIndex]?.subItems || [];

    const isMasterRelated = masterSubItems.some((subItem) => {
      const basePath = subItem.path.split("/")[1].replace("-list", "");
      return (
        location.pathname === subItem.path ||
        location.pathname === `/${basePath}` ||
        location.pathname === `/${basePath}-add` ||
        location.pathname === `/${basePath}-edit` ||
        location.pathname === `/${basePath}-view` ||
        location.pathname === `/${basePath}-details` ||
        location.pathname === `/${basePath}-quotation-view` ||
        location.pathname.startsWith(`/${basePath}-edit/`) ||
        location.pathname.startsWith(`/${basePath}/`)
      );
    });

    const isProductRelated = productSubItems.some((subItem) => {
      const basePath = subItem.path.split("/")[1].replace("-list", "");
      return (
        location.pathname === subItem.path ||
        location.pathname === `/${basePath}` ||
        location.pathname === `/${basePath}-add` ||
        location.pathname === `/${basePath}-edit` ||
        location.pathname === `/${basePath}-view` ||
        location.pathname === `/${basePath}-details` ||
        location.pathname === `/${basePath}-quotation-view` ||
        location.pathname.startsWith(`/${basePath}-edit/`) ||
        location.pathname.startsWith(`/${basePath}/`)
      );
    });

    const isReportsRelated = reportsSubItems.some((subItem) => {
      return (
        location.pathname === subItem.path ||
        (subItem.path === "/lead-summary" &&
          location.pathname === "/lead-summary-details")
      );
    });

    if (isMasterRelated) {
      setOpenSubmenu({ type: "main", index: masterIndex });
    } else if (isProductRelated) {
      setOpenSubmenu({ type: "main", index: productIndex });
    } else if (isReportsRelated) {
      setOpenSubmenu({ type: "main", index: reportsIndex });
    } else {
      setOpenSubmenu(null);
    }
  }, [navItems, location.pathname]);

  const handleSubmenuToggle = (index: number, menuType: "main" | "others") => {
    setOpenSubmenu((prevOpenSubmenu) =>
      prevOpenSubmenu &&
        prevOpenSubmenu.type === menuType &&
        prevOpenSubmenu.index === index
        ? null
        : { type: menuType, index }
    );
  };

  const renderMenuItems = (items: NavItem[], menuType: "main" | "others") => (
    <ul className={menuListStyles}>
      {items.map((nav, index) => {
        const isSubItemActive = nav.subItems?.some((subItem) =>
          isActive(subItem.path)
        );
        const isMasterRelated =
          nav.name === "Master" &&
          nav.subItems?.some((subItem) => {
            const basePath = subItem.path.split("/")[1].replace("-list", "");
            return (
              location.pathname === subItem.path ||
              location.pathname === `/${basePath}` ||
              location.pathname === `/${basePath}-add` ||
              location.pathname === `/${basePath}-edit` ||
              location.pathname === `/${basePath}-view` ||
              location.pathname === `/${basePath}-details` ||
              location.pathname === `/${basePath}-quotation-view` ||
              location.pathname.startsWith(`/${basePath}-edit/`) ||
              location.pathname.startsWith(`/${basePath}/`)
            );
          });
        const isProductRelated =
          nav.name === "Product" &&
          nav.subItems?.some((subItem) => {
            const basePath = subItem.path.split("/")[1].replace("-list", "");
            return (
              location.pathname === subItem.path ||
              location.pathname === `/${basePath}` ||
              location.pathname === `/${basePath}-add` ||
              location.pathname === `/${basePath}-edit` ||
              location.pathname === `/${basePath}-view` ||
              location.pathname === `/${basePath}-details` ||
              location.pathname === `/${basePath}-quotation-view` ||
              location.pathname.startsWith(`/${basePath}-edit/`) ||
              location.pathname.startsWith(`/${basePath}/`)
            );
          });
        const isReportsRelated =
          nav.name === "Reports" &&
          nav.subItems?.some((subItem) => {
            return (
              location.pathname === subItem.path ||
              location.pathname === "/lead-summary-details"
            );
          });

        return (
          <li key={nav.name}>
            {nav.subItems ? (
              <button
                onClick={() => handleSubmenuToggle(index, menuType)}
                className={`${menuItemStyles} group ${openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "menu-item-active"
                    : isSubItemActive ||
                      isMasterRelated ||
                      isProductRelated ||
                      isReportsRelated
                      ? "menu-item-active"
                      : "menu-item-inactive"
                  } cursor-pointer ${!isExpanded && !isHovered
                    ? "lg:justify-center"
                    : "lg:justify-start"
                  }`}
                style={{
                  backgroundColor:
                    (openSubmenu?.type === menuType &&
                      openSubmenu?.index === index) ||
                      isSubItemActive ||
                      isMasterRelated ||
                      isProductRelated ||
                      isReportsRelated
                      ? isDarkMode
                        ? "#374151"
                        : settings.menuActiveColor || "#f8f7f7"
                      : undefined,
                  color: isDarkMode
                    ? "#d1d5db"
                    : settings.fontColor || "#000000",
                }}
              >
                <span
                  className={`${menuItemIconStyles} ${openSubmenu?.type === menuType &&
                      openSubmenu?.index === index
                      ? "menu-item-icon-active"
                      : isSubItemActive ||
                        isMasterRelated ||
                        isProductRelated ||
                        isReportsRelated
                        ? "menu-item-icon-active"
                        : "menu-item-icon-inactive"
                    }`}
                  style={{
                    color: isDarkMode
                      ? "#d1d5db"
                      : settings.fontColor || "#000000",
                  }}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span
                    className={menuItemTextStyles}
                    style={{
                      color: isDarkMode
                        ? "#d1d5db"
                        : settings.fontColor || "#000000",
                    }}
                  >
                    {nav.name}
                  </span>
                )}
                {(isExpanded || isHovered || isMobileOpen) && (
                  <ChevronDownIcon
                    className={`${chevronStyles} ${openSubmenu?.type === menuType &&
                        openSubmenu?.index === index
                        ? "rotate-180 text-brand-500"
                        : ""
                      }`}
                    style={{
                      color: isDarkMode
                        ? "#d1d5db"
                        : settings.fontColor || "#000000",
                    }}
                  />
                )}
              </button>
            ) : nav.path ? (
              <Link
                to={nav.path}
                className={`${menuItemStyles} group ${isActive(nav.path) ? "menu-item-active" : "menu-item-inactive"
                  }`}
                style={{
                  backgroundColor: isActive(nav.path)
                    ? isDarkMode
                      ? "#374151"
                      : settings.menuActiveColor || "#f8f7f7"
                    : undefined,
                  color: isDarkMode
                    ? "#d1d5db"
                    : settings.fontColor || "#000000",
                }}
              >
                <span
                  className={`${menuItemIconStyles} ${isActive(nav.path)
                      ? "menu-item-icon-active"
                      : "menu-item-icon-inactive"
                    }`}
                  style={{
                    color: isDarkMode
                      ? "#d1d5db"
                      : settings.fontColor || "#000000",
                  }}
                >
                  {nav.icon}
                </span>
                {(isExpanded || isHovered || isMobileOpen) && (
                  <span
                    className={menuItemTextStyles}
                    style={{
                      color: isDarkMode
                        ? "#d1d5db"
                        : settings.fontColor || "#000000",
                    }}
                  >
                    {nav.name}
                  </span>
                )}
              </Link>
            ) : null}
            {nav.subItems && (
              <div
                ref={(el) => (subMenuRefs.current[`${menuType}-${index}`] = el)}
                className={`${submenuContainerStyles} ${openSubmenu?.type === menuType && openSubmenu?.index === index
                    ? "max-h-[1000px] opacity-100"
                    : "max-h-0 opacity-0"
                  }`}
              >
                <ul className={submenuListStyles}>
                  {nav.subItems.map((subItem) => {
                    const isSubItemActive = isActive(subItem.path);
                    return (
                      <li key={subItem.name}>
                        <Link
                          to={subItem.path}
                          className={`${submenuItemStyles} ${isSubItemActive
                              ? "menu-dropdown-item-active"
                              : "menu-dropdown-item-inactive"
                            }`}
                          style={{
                            backgroundColor: isSubItemActive
                              ? isDarkMode
                                ? "#374151"
                                : settings.menuActiveColor || "#e6e6e6"
                              : undefined,
                            color: isDarkMode
                              ? "#d1d5db"
                              : settings.fontColor || "#000000",
                          }}
                        >
                          {subItem.name}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <aside
      className={`${asideStyles} ${isExpanded || isMobileOpen
          ? "w-[240px]"
          : isHovered
            ? "w-[240px]"
            : "w-[80px]"
        } ${isMobileOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0`}
      style={{
        backgroundColor: isDarkMode
          ? "#1f2937"
          : settings.sidebarColor || "#ffffff",
      }}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`${logoContainerStyles} ${!isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
          } ${isMobileOpen ? "hidden" : "block"}`}
      >
        <Link to="/" className="block w-full">
          {(isExpanded || isHovered || isMobileOpen) ? (
            <div
              className={`w-full flex items-center ${displayName ? "justify-start gap-2 px-4" : "justify-center"
                }`}
            >
              <img
                className="dark:hidden h-10"
                src={logoSrc}
                alt="Logo"
              />
              <img
                className="hidden dark:block h-12"
                src={logoSrc}
                alt="Logo"
              />
              {displayName && (
                <span
                  className="text-2xl font-semibold"
                  style={{
                    color: isDarkMode
                      ? "#d1d5db"
                      : settings.fontColor || "#000000",
                  }}
                >
                  {displayName}
                </span>
              )}
            </div>
          ) : (
            <div className="w-full flex justify-center">
              <img src={logoSrc} alt="Logo" className="w-5 h-8" />
            </div>
          )}
        </Link>


      </div>
      <div className="flex flex-col overflow-y-auto duration-300 ease-linear no-scrollbar">
        <nav className={navStyles}>
          <div className="flex flex-col gap-4">
            <div>{renderMenuItems(navItems, "main")}</div>
          </div>
        </nav>
      </div>
    </aside>
  );
};

export default AppSidebar;